import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { AccountGifiReportType, AccountInputRow, AccountRow, AccountType, GifiRow, NamirasoftERPMetaDatabase, PartyTypeRow, TagGroupRow } from "namirasoft-erp";
import { NSBox, NSBoxBoolean, NSBoxEntity, NSBoxEnum, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpEntities } from "../ErpEntities";
import { ErpLayout } from '../ErpLayout';
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";

interface Props extends NSARouterMakerResult, ErpRouterProps { isEdit: boolean; }

export default class AccountEditPage extends Component<Props>
{
    ParentAccount = createRef<NSBoxEntity<AccountRow>>();
    Code = createRef<NSBoxString>();
    Name = createRef<NSBoxString>();
    IsGroup = createRef<NSBoxBoolean>();
    Type = createRef<NSBoxEnum<AccountType>>();
    PartyTypes = createRef<NSBoxEntity<PartyTypeRow>>();
    TagGroups = createRef<NSBoxEntity<TagGroupRow>>();
    Description = createRef<NSBoxTextArea>();
    Enabled = createRef<NSBoxBoolean>();
    Gifi = createRef<NSBoxEntity<GifiRow>>();
    GifiReportTypeRef = createRef<NSBoxEnum<AccountGifiReportType>>();
    GifiIncludeClosing = createRef<NSBoxBoolean>();

    override render()
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);

        let gifiTabContent = () => (
            <NSColumn>
                <NSBoxEntity<GifiRow>
                    ref={this.Gifi} title="GIFI Code" required={false} multiple={false}
                    table_name="gifi" table={db.tables.gifi}
                    list={(filters, page, size, sorts) =>
                    {
                        let f: FilterItem[] = filters ? [...filters] : [];
                        f.push(new FilterItem(db.tables.gifi, db.tables.gifi.columns.active, false, FilterItemOperator.all.equals, "1"));
                        return this.props.servers.erp.gifi.List(f, page, size, sorts, null);
                    }}
                    getValue={(item) => item.id} getTitle={(item) => `${item.id} - ${item.name}`} getSort={() => null}
                    placeholder="Search GIFI by code, name, or description"
                    style={{ width: NSBox.width.double }}
                />
                <NSBoxEnum<AccountGifiReportType>
                    ref={this.GifiReportTypeRef}
                    title="GIFI Report Type"
                    required={false}
                    multiple={false}
                    getEnumObject={() => AccountGifiReportType}
                    defaultValue={AccountGifiReportType.BalanceRemained}
                    style={{ width: NSBox.width.double }}
                />
                <NSBoxBoolean
                    ref={this.GifiIncludeClosing}
                    required
                    checked={true}
                    title="Include Closing Entries"
                    style={{ width: NSBox.width.double }}
                />
            </NSColumn>
        );

        return (
            <ErpLayout<AccountRow, AccountInputRow>
                {...this.props}
                mode={ErpScopeMode.Select}
                fields={[ErpScopeField.Company, ErpScopeField.Branch]}
                pages={{
                    entity: ErpEntities.account(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "GIFI",
                                            clicked: true,
                                            getContent: gifiTabContent,
                                        },
                                    ],
                                },
                                editor: {
                                    getInputRow: (): AccountInputRow =>
                                    {
                                        let ids = this.PartyTypes.current?.getValueAll() ?? [];
                                        let tagGroupIds = this.TagGroups.current?.getValueAll() ?? [];
                                        return {
                                            company_id: ErpScope.getCompanyId() ?? "",
                                            branch_id: ErpScope.getBranchId() ?? "",
                                            account_id: this.ParentAccount.current?.getValueOne(false) ?? null,
                                            code: this.Code.current?.getValue() ?? "",
                                            name: this.Name.current?.getValue() ?? "",
                                            is_group: this.IsGroup.current?.getValueBool() ?? false,
                                            type: this.Type.current?.getValueEnumOne(AccountType, AccountType.Asset) ?? AccountType.Asset,
                                            enabled: this.Enabled.current?.getValueBool() ?? true,
                                            description: this.Description.current?.getValue() ?? null,
                                            party_type_ids: ids.length > 0 ? ids : null,
                                            tag_group_ids: tagGroupIds.length > 0 ? tagGroupIds : null,
                                            gifi: this.Gifi.current?.getValueOne(false) ?? null,
                                            gifi_report_type: this.GifiReportTypeRef.current?.getValueEnumOne(AccountGifiReportType, null as any) ?? null,
                                            gifi_include_closing: this.GifiIncludeClosing.current?.getValueBool() ?? true,
                                            account_category: [],
                                            account_tag: [],
                                            account_field: [],
                                        };
                                    },
                                    setRow: (row: AccountRow) =>
                                    {
                                        ErpScope.set({ company_id: row.company_id, branch_id: row.branch_id });
                                        this.ParentAccount.current?.setValueOne(row.account_id);
                                        this.Code.current?.setValue(row.code);
                                        this.Name.current?.setValue(row.name);
                                        this.IsGroup.current?.setValueBool(row.is_group);
                                        this.Type.current?.setValueOne(row.type);
                                        let partyTypeIds = row.party_type_ids ?? [];
                                        this.PartyTypes.current?.setValueAll(partyTypeIds);
                                        let tagGroupIds = row.tag_group_ids ?? [];
                                        this.TagGroups.current?.setValueAll(tagGroupIds);
                                        this.Description.current?.setValue(row.description);
                                        this.Enabled.current?.setValueBool(row.enabled);
                                        this.Gifi.current?.setValueOne(row.gifi);
                                        this.GifiReportTypeRef.current?.setValueOne(row.gifi_report_type ?? AccountGifiReportType.BalanceRemained);
                                        this.GifiIncludeClosing.current?.setValueBool((row as unknown as { gifi_include_closing?: boolean }).gifi_include_closing ?? true);
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxEntity<AccountRow>
                        ref={this.ParentAccount} title="Parent Account" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-parent-account" }} required={false} multiple={false}
                        table_name="account" table={db.tables.account}
                        list={(filters, page, size, sorts) =>
                        {
                            let f: FilterItem[] = filters ? [...filters] : [];
                            f = ErpScope.pushFilter(f, db.tables.account, "company_id", ErpScope.getCompanyId());
                            f = ErpScope.pushFilter(f, db.tables.account, "branch_id", ErpScope.getBranchId());
                            f.push(new FilterItem(db.tables.account, db.tables.account.columns.enabled, false, FilterItemOperator.all.equals, "1"));
                            f.push(new FilterItem(db.tables.account, db.tables.account.columns.is_group, false, FilterItemOperator.all.equals, "1"));
                            return this.props.servers.erp.account.List(f, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSRow style={{ gap: 8 }}>
                        <NSBoxString ref={this.Code} required title="Code" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-code" }} style={{ width: NSBox.width.one }} />
                        <NSBoxString ref={this.Name} required title="Name" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-name" }} style={{ width: NSBox.width.one }} />
                    </NSRow>
                    <NSRow style={{ gap: 8 }}>
                        <NSBoxBoolean ref={this.IsGroup} required title="Is Group" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-is-group" }} style={{ width: NSBox.width.one }} />
                        <NSBoxEnum<AccountType>
                            ref={this.Type} title="Type" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-type" }} required multiple={false}
                            getEnumObject={() => AccountType}
                            style={{ width: NSBox.width.one }}
                        />
                    </NSRow>
                    <NSBoxEntity<PartyTypeRow>
                        ref={this.PartyTypes} title="Party Types" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-party-types" }} required={false} multiple={true}
                        table_name="party_type" table={db.tables.party_type}
                        list={(filters, page, size, sorts) =>
                        {
                            let f: FilterItem[] = filters ? [...filters] : [];
                            f = ErpScope.pushFilter(f, db.tables.party_type, "company_id", ErpScope.getCompanyId());
                            f = ErpScope.pushFilter(f, db.tables.party_type, "branch_id", ErpScope.getBranchId());
                            return this.props.servers.erp.party_type.List(f, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxEntity<TagGroupRow>
                        ref={this.TagGroups} title="Tag Groups" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-tag-groups" }} required={false} multiple={true}
                        table_name="tag_group" table={db.tables.tag_group}
                        list={(filters, page, size, sorts) =>
                        {
                            let f: FilterItem[] = filters ? [...filters] : [];
                            f = ErpScope.pushFilter(f, db.tables.tag_group, "company_id", ErpScope.getCompanyId());
                            f = ErpScope.pushFilter(f, db.tables.tag_group, "branch_id", ErpScope.getBranchId());
                            return this.props.servers.erp.tag_group.List(f, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxTextArea ref={this.Description} required={false} title="Description" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-description" }} style={{ width: NSBox.width.double }} />
                    <NSBoxBoolean ref={this.Enabled} required checked={true} title="Enabled" info={{ link: "https://namirasoft.com/erp/console-guide/account/#field-enabled" }} style={{ width: NSBox.width.double }} />
                </NSColumn>
            </ErpLayout>
        );
    }
}